import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { ZonaRoja, PointZone, PolygonZone, LineStringZone, ZonasRojasJson } from './zonas-rojas.types';

declare var google: any;

const CACHE_KEY = 'zonas_rojas_cache';
const CACHE_TTL_MS = 86400000;

@Injectable()
export class ZonasRojasService {

    private zonas: ZonaRoja[] = null;
    private cargando: Promise<ZonaRoja[]> = null;

    constructor(private http: HttpClient) {}

    cargarZonas(): Promise<ZonaRoja[]> {
        if (this.zonas !== null) {
            return Promise.resolve(this.zonas);
        }
        if (this.cargando !== null) {
            return this.cargando;
        }
        this.cargando = this.http.get<ZonasRojasJson>(environment.zonasRojasJsonUrl)
            .toPromise()
            .then(data => {
                this.zonas = data.zonas;
                this.guardarEnLocalStorage(data.zonas);
                return this.zonas;
            })
            .catch(err => {
                console.warn('ZonasRojasService: fallo al cargar JSON, intentando localStorage', err);
                this.cargando = null;
                const fallback = this.leerDeLocalStorage();
                if (fallback !== null) {
                    this.zonas = fallback;
                    return fallback;
                }
                return [];
            });
        return this.cargando;
    }

    checkPoint(lat: number, lng: number): ZonaRoja[] {
        if (!this.zonas || this.zonas.length === 0) {
            return [];
        }
        const punto = new google.maps.LatLng(lat, lng);
        return this.zonas.filter(zona => {
            if (zona.type === 'point') {
                return this.puntoEnRadio(punto, zona as PointZone);
            }
            if (zona.type === 'polygon') {
                return this.puntoEnPoligono(punto, zona as PolygonZone);
            }
            return false;
        });
    }

    checkRoute(path: any[]): Promise<{ red: ZonaRoja[], yellow: ZonaRoja[] }> {
        if (!this.zonas || this.zonas.length === 0 || !path || path.length === 0) {
            return Promise.resolve({ red: [], yellow: [] });
        }
        const samples = this.interpolatePath(path, 100);
        const red: ZonaRoja[] = [];
        const yellow: ZonaRoja[] = [];
        const zonas = this.zonas;
        let i = 0;
        const CHUNK = 10;
        return new Promise<{ red: ZonaRoja[], yellow: ZonaRoja[] }>(resolve => {
            const tick = () => {
                const end = Math.min(i + CHUNK, zonas.length);
                while (i < end) {
                    const r = this.checkZonaContraRuta(zonas[i], samples);
                    if (r === 'red') { red.push(zonas[i]); }
                    else if (r === 'yellow') { yellow.push(zonas[i]); }
                    i++;
                }
                if (i < zonas.length) {
                    setTimeout(tick, 0);
                } else {
                    resolve({ red, yellow });
                }
            };
            setTimeout(tick, 0);
        });
    }

    private checkZonaContraRuta(zona: ZonaRoja, samples: any[]): 'red' | 'yellow' | 'none' {
        if (zona.type === 'point') {
            return this.checkPointZonaContraRuta(zona as PointZone, samples);
        }
        if (zona.type === 'polygon') {
            return this.checkPolygonZonaContraRuta(zona as PolygonZone, samples);
        }
        if (zona.type === 'linestring') {
            return this.checkLinestringZonaContraRuta(zona as LineStringZone, samples);
        }
        return 'none';
    }

    private checkPointZonaContraRuta(zona: PointZone, samples: any[]): 'red' | 'yellow' | 'none' {
        const centro = new google.maps.LatLng(zona.lat, zona.lng);
        let minDist = Infinity;
        for (const sample of samples) {
            const d = google.maps.geometry.spherical.computeDistanceBetween(sample, centro);
            if (d < minDist) { minDist = d; }
        }
        if (minDist <= zona.radius) { return 'red'; }
        if (minDist <= zona.radius * 1.25) { return 'yellow'; }
        return 'none';
    }

    private checkPolygonZonaContraRuta(zona: PolygonZone, samples: any[]): 'red' | 'yellow' | 'none' {
        const poligono = new google.maps.Polygon({ paths: zona.path });
        for (const sample of samples) {
            if (google.maps.geometry.poly.containsLocation(sample, poligono)) { return 'red'; }
        }
        const POLYGON_NEAR_METERS = 100;
        const boundarySampled = this.interpolatePath(zona.path.map(p => new google.maps.LatLng(p.lat, p.lng)), 50);
        for (const sample of samples) {
            for (const bp of boundarySampled) {
                if (google.maps.geometry.spherical.computeDistanceBetween(sample, bp) <= POLYGON_NEAR_METERS) {
                    return 'yellow';
                }
            }
        }
        return 'none';
    }

    private checkLinestringZonaContraRuta(zona: LineStringZone, samples: any[]): 'red' | 'yellow' | 'none' {
        const zonaPath = this.interpolatePath(zona.path.map(p => new google.maps.LatLng(p.lat, p.lng)), 50);
        let minDist = Infinity;
        for (const sample of samples) {
            for (const zp of zonaPath) {
                const d = google.maps.geometry.spherical.computeDistanceBetween(sample, zp);
                if (d < minDist) { minDist = d; }
            }
        }
        if (minDist <= zona.bufferMeters) { return 'red'; }
        if (minDist <= zona.bufferMeters * 1.25) { return 'yellow'; }
        return 'none';
    }

    private interpolatePath(path: any[], stepMeters: number): any[] {
        if (path.length === 0) { return []; }
        const result: any[] = [path[0]];
        for (let i = 0; i < path.length - 1; i++) {
            const p1 = path[i];
            const p2 = path[i + 1];
            const segDist = google.maps.geometry.spherical.computeDistanceBetween(p1, p2);
            if (segDist >= 1) {
                const steps = Math.floor(segDist / stepMeters);
                for (let j = 1; j <= steps; j++) {
                    result.push(google.maps.geometry.spherical.interpolate(p1, p2, (j * stepMeters) / segDist));
                }
            }
            result.push(p2);
        }
        return result;
    }

    private puntoEnRadio(punto: any, zona: PointZone): boolean {
        const centro = new google.maps.LatLng(zona.lat, zona.lng);
        const distancia = google.maps.geometry.spherical.computeDistanceBetween(punto, centro);
        return distancia <= zona.radius;
    }

    private puntoEnPoligono(punto: any, zona: PolygonZone): boolean {
        const poligono = new google.maps.Polygon({ paths: zona.path });
        return google.maps.geometry.poly.containsLocation(punto, poligono);
    }

    private guardarEnLocalStorage(zonas: ZonaRoja[]): void {
        try {
            localStorage.setItem(CACHE_KEY, JSON.stringify({
                timestamp: Date.now(),
                zonas: zonas
            }));
        } catch (e) {}
    }

    private leerDeLocalStorage(): ZonaRoja[] | null {
        try {
            const raw = localStorage.getItem(CACHE_KEY);
            if (!raw) {
                return null;
            }
            const cached = JSON.parse(raw);
            if (!cached || !cached.zonas || !cached.timestamp) {
                return null;
            }
            if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
                return null;
            }
            return cached.zonas;
        } catch (e) {
            return null;
        }
    }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/integration/zonas-rojas/zonas-rojas.service.ts